import React, { useEffect, useMemo } from "react";
import { Outlet, matchPath, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import "./AppLayout.scss";
import AppFooter from "../../AppFooter/AppFooter";
import { usePwaContext } from "../../../context/pwa-context";
import {
  checkBrowser,
  checkOs,
  isGamesPagePath,
  openApp,
  removeTrailingSlash,
} from "../../../utils/helpers";
import UseAppNotice from "../../UseAppNotice/UseAppNotice";
import {
  getStoredHasShownNotificationPrompt,
  getStoredPwaInstallOrigin,
  setStoredHasShownNotificationPrompt,
} from "../../../utils/storage";
import { ROUTES } from "../../../utils/constants";
import GameControllerIcon from "../../icons/GameControllerIcon";
import WalletIcon from "../../icons/WalletIcon";
import HyperMiniIcon from "../../icons/HyperMiniIcon";
import NotificationFlow from "../../notification-flow/NotificationFlow";
import useNotifications from "../../../hooks/useNotifications";
import useAuth from "../../../hooks/useAuth";
import useDeepLink from "../../../hooks/useDeepLink";
import LoadingOverlay, {
  OverlayVariant,
} from "../../LoadingOverlay/LoadingOverlay";
import DownloadAppPrompt, {
  PROMPT_DOWNLOAD_SCENARIOS,
} from "../../DownloadAppPrompt/DownloadAppPrompt";
import useDisclosure from "../../../hooks/useDisclosure";
import useUserProfileQuery from "../../../react-query/queries/use-user-profile";
import { renderHeaders } from "../../../utils/renderHeaders";
import { useAtom } from "jotai";
import { userHasClosedAppNoticeAtom } from "../../../jotai/atoms/user";
import { OperatingSystems } from "../../../utils/vendor";
import ScrollToTop from "../../ScrollToTop";
import useUtmAttribution from "../../../hooks/useUtmAttribution";
import usePhoenixClickAttribution from "../../../hooks/usePhoenixClickAttribution";
import { cx } from "../../../utils/cx";

const useAppNavigationRoutes = () => {
  const { t } = useTranslation("common");

  return useMemo(
    () => [
      {
        name: t("home"),
        icon: HyperMiniIcon,
        href: ROUTES.ROOT.DASHBOARD,
        isProtected: true,
        requiresApp: false,
      },
      {
        name: t("games"),
        icon: GameControllerIcon,
        href: ROUTES.GAMES.ROOT,
        isProtected: false,
        requiresApp: false,
      },
      {
        name: t("wallet"),
        icon: WalletIcon,
        href: ROUTES.WALLET.ROOT,
        isProtected: false,
        requiresApp: true,
      },
    ],
    [t]
  );
};

const HOMEPAGE = ROUTES.ROOT.HOME;
const GAMES_PAGE = ROUTES.GAMES.ROOT;
const DASHBOARD_PAGE = ROUTES.ROOT.DASHBOARD;

function AppLayout() {
  useUserProfileQuery();
  useUtmAttribution();
  usePhoenixClickAttribution();
  const { slug } = useParams();
  const location = useLocation();
  const { pathname } = location;
  const { t } = useTranslation("common");
  const { isLoggedIn: userIsLoggedIn } = useAuth();
  const { isTryingToOpenMobileApp } = useDeepLink();
  const appNavigationRoutes = useAppNavigationRoutes();
  const [userHasClosedAppNotice, setUserHasClosedAppNotice] = useAtom(
    userHasClosedAppNoticeAtom
  );
  const os = checkOs();
  const browser = checkBrowser();
  const browserIsSafari = useMemo(() => !!browser?.match(/Safari/i), [browser]);

  const isGameLandingPage = !!matchPath(
    ROUTES.GAMES.GAME_LANDING_PAGE,
    pathname
  );

  const isGamesPageRoute = isGamesPagePath(pathname, slug);
  const isDownloadPageRoute =
    removeTrailingSlash(pathname) === ROUTES.ROOT.DOWNLOAD;

  const {
    notificationsAreEnabled,
    showPeriodicNotificationPrompt,
    ...notifications
  } = useNotifications();

  const {
    userIsInPwa,
    appInstallHasCompleted,
    doesRelatedAppExist,
    resetInstallationParams,
  } = usePwaContext();

  const {
    isOpen: isDownloadAppPromptOpen,
    onOpen: openDownloadAppPrompt,
    onClose: closeDownloadAppPrompt,
  } = useDisclosure();
  const {
    isOpen: isAppNoticeOpen,
    onOpen: openAppNotice,
    onClose: closeAppNotice,
  } = useDisclosure();

  const pathIsANavRoute = useMemo(
    () =>
      pathname === HOMEPAGE ||
      removeTrailingSlash(pathname) === `${GAMES_PAGE}/${slug}` ||
      !!appNavigationRoutes.find(
        (route) => removeTrailingSlash(pathname) === route.href
      ),
    [appNavigationRoutes, pathname, slug]
  );

  const showContentPage = isDownloadPageRoute && !userIsLoggedIn;
  const canShowPwaNavAndBanner =
    pathIsANavRoute && !showContentPage && !isGameLandingPage;

  const handleCloseAppNotice = () => {
    setUserHasClosedAppNotice(true);
    closeAppNotice();
  };

  // Trigger the prompt to turn on notifications automatically once per session and once within 24 hours across different sessions.
  useEffect(() => {
    const hasShownNotificationPrompt = getStoredHasShownNotificationPrompt();
    if (
      !userIsLoggedIn ||
      !pathIsANavRoute ||
      isGameLandingPage ||
      notificationsAreEnabled ||
      hasShownNotificationPrompt
    )
      return;

    showPeriodicNotificationPrompt(24);
    setStoredHasShownNotificationPrompt(true);
  }, [
    userIsLoggedIn,
    pathIsANavRoute,
    isGameLandingPage,
    notificationsAreEnabled,
    showPeriodicNotificationPrompt,
  ]);

  // Handle showing the Get app/Use app notice
  useEffect(() => {
    (async () => {
      if (isGameLandingPage) {
        closeAppNotice();
        return;
      }
      const appExists = await doesRelatedAppExist();
      if (userHasClosedAppNotice || browserIsSafari) return;

      if (userIsLoggedIn) {
        canShowPwaNavAndBanner ? openAppNotice() : closeAppNotice();
        return;
      }

      if (
        (!appExists && pathname === DASHBOARD_PAGE) ||
        (appExists && pathIsANavRoute)
      ) {
        openAppNotice();
      } else {
        closeAppNotice();
      }
    })();
  }, [
    isGameLandingPage,
    pathname,
    pathIsANavRoute,
    browserIsSafari,
    userIsLoggedIn,
    userHasClosedAppNotice,
    doesRelatedAppExist,
    openAppNotice,
    closeAppNotice,
    canShowPwaNavAndBanner,
  ]);

  // Open the PWA 3 seconds after installation has completed.
  useEffect(() => {
    const pwaInstallOrigin = getStoredPwaInstallOrigin();
    // eslint-disable-next-line no-undef
    let openAppTimer: NodeJS.Timeout;
    if (appInstallHasCompleted && !userIsInPwa) {
      openAppTimer = setTimeout(() => {
        resetInstallationParams();
        openApp(pwaInstallOrigin || ROUTES.GAMES.ROOT);
      }, 3000);
    }

    return () => {
      clearTimeout(openAppTimer);
    };
  }, [appInstallHasCompleted, userIsInPwa, resetInstallationParams]);

  if (isTryingToOpenMobileApp && pathIsANavRoute) {
    return (
      <div className="LoaderWrapper MobileOnly">
        <LoadingOverlay variant={OverlayVariant.BLOCKS} />
      </div>
    );
  }

  return (
    <>
      <ScrollToTop />
      {renderHeaders({
        showAppBanner: pathIsANavRoute,
        title: t("metadata.battle_friends_in_fun_games_and_make_money"),
        description: t(
          "metadata.battle_friends_and_frenemies_in_fun_games_to_win_the_prize_pool"
        ),
        ogDescription: t(
          "metadata.battle_friends_and_frenemies_in_fun_casual_games_to_win_$1k"
        ),
        ogTitle:
          "Battle Friends in Fun Games & Make Money | Hyper by Metaverse Magna",
      })}

      <div
        className={cx(
          "AppLayout",
          !showContentPage &&
            !isDownloadPageRoute &&
            !isGameLandingPage &&
            "MobileOnly",
          os === OperatingSystems.Ios && "Ios",
          userIsInPwa && "Pwa",
          !canShowPwaNavAndBanner && "ContentPageShell",
          isGameLandingPage && "GameLandingPageShell"
        )}
      >
        <UseAppNotice isOpen={isAppNoticeOpen} onClose={handleCloseAppNotice} />
        <div
          className={`position-relative MainContent ${
            isGamesPageRoute ? "GamesPageContent" : ""
          } ${isAppNoticeOpen ? "BannerIsShowing" : ""} ${
            canShowPwaNavAndBanner ? "NavIsShowing" : ""
          }`}
        >
          <Outlet />
          {canShowPwaNavAndBanner && (
            <AppFooter routes={appNavigationRoutes} />
          )}
        </div>
        {!isGameLandingPage && (
          <>
            <NotificationFlow
              {...notifications}
              handleGetApp={openDownloadAppPrompt}
            />
            <DownloadAppPrompt
              isOpen={isDownloadAppPromptOpen}
              onOpen={openDownloadAppPrompt}
              onClose={closeDownloadAppPrompt}
              showNotificationsFlow={false}
              promptDownloadScenario={PROMPT_DOWNLOAD_SCENARIOS.LOGGED_IN.OTHERS}
            />
          </>
        )}
      </div>
    </>
  );
}

export default AppLayout;
